import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { logAdminActivity } from '../firebase/helpers';

const HERO_TEXT_COLLECTION = 'heroTextSettings';
const HERO_TEXT_DOC_ID = 'main';

// Default hero text values
const DEFAULT_HERO_TEXT = {
  titleLine1: 'Where',
  titleHighlight: 'TIME',
  titleLine2: 'pays',
  description: "We don't wait for success, we accelerate it.",
  createdAt: null,
  updatedAt: null,
  updatedBy: null,
  updatedByEmail: null
};

/**
 * Get hero text settings from Firestore
 * @returns {Promise<Object>} Hero text settings
 */
export const getHeroText = async () => {
  try {
    const docRef = doc(db, HERO_TEXT_COLLECTION, HERO_TEXT_DOC_ID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        titleLine1: data.titleLine1 || DEFAULT_HERO_TEXT.titleLine1,
        titleHighlight: data.titleHighlight || DEFAULT_HERO_TEXT.titleHighlight,
        titleLine2: data.titleLine2 || DEFAULT_HERO_TEXT.titleLine2,
        description: data.description || DEFAULT_HERO_TEXT.description,
        updatedAt: data.updatedAt,
        updatedBy: data.updatedBy,
        updatedByEmail: data.updatedByEmail
      };
    } else {
      // Create default document if it doesn't exist
      await setDoc(docRef, {
        ...DEFAULT_HERO_TEXT,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return DEFAULT_HERO_TEXT;
    }
  } catch (error) {
    console.error('Error getting hero text:', error);
    // Return defaults on error
    return DEFAULT_HERO_TEXT;
  }
};

/**
 * Update hero text settings (Admin only)
 * @param {Object} heroTextData - New hero text data
 * @param {string} adminId - Admin user ID
 * @param {string} adminEmail - Admin email
 * @returns {Promise<Object>} Update result
 */
export const updateHeroText = async (heroTextData, adminId, adminEmail) => {
  try {
    const docRef = doc(db, HERO_TEXT_COLLECTION, HERO_TEXT_DOC_ID);
    
    const updateData = {
      titleLine1: heroTextData.titleLine1 || DEFAULT_HERO_TEXT.titleLine1,
      titleHighlight: heroTextData.titleHighlight || DEFAULT_HERO_TEXT.titleHighlight,
      titleLine2: heroTextData.titleLine2 || DEFAULT_HERO_TEXT.titleLine2,
      description: heroTextData.description || DEFAULT_HERO_TEXT.description,
      updatedAt: serverTimestamp(),
      updatedBy: adminId,
      updatedByEmail: adminEmail
    };

    // If document doesn't exist, include createdAt
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      updateData.createdAt = serverTimestamp();
    }

    await setDoc(docRef, updateData, { merge: true });

    // Log admin activity
    await logAdminActivity({
      adminId: adminId,
      adminEmail: adminEmail,
      action: 'updateHeroText',
      changes: {
        titleLine1: updateData.titleLine1,
        titleHighlight: updateData.titleHighlight,
        titleLine2: updateData.titleLine2,
        description: updateData.description
      },
      description: `Updated hero text settings: "${updateData.titleLine1} ${updateData.titleHighlight} ${updateData.titleLine2}" - "${updateData.description}"`
    });

    return {
      success: true,
      message: 'Hero text updated successfully'
    };
  } catch (error) {
    console.error('Error updating hero text:', error);
    throw new Error('Failed to update hero text: ' + error.message);
  }
};

/**
 * Reset hero text to defaults (Admin only)
 * @param {string} adminId - Admin user ID
 * @param {string} adminEmail - Admin email
 * @returns {Promise<Object>} Reset result
 */
export const resetHeroText = async (adminId, adminEmail) => {
  try {
    return await updateHeroText(DEFAULT_HERO_TEXT, adminId, adminEmail);
  } catch (error) {
    console.error('Error resetting hero text:', error);
    throw error;
  }
};

